import CountryCurrency from "@data/country-currency.json";
import type Address from "@interfaces/Address";
import type Coupon from "@interfaces/Coupon";
import { type CreditCard, type Wallet } from "@interfaces/Payment";
import { useConfigurationStore } from "@stores/configuration";
import axios from "@utilities/axios";
import { encryptedStorage, hashKey } from "@utilities/encrypted-storage";
import { getPaymentPublishableKey, isDebugMode } from "@utilities/helper";
import { appStatusApp } from "@utilities/paymentConfiguration";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type CountryCurrencyType = Record<string, string>;

const skipCards: string[] = ["ADD MORE"];
export interface AccountDetailState {
  license: {
    cli: string;
    name: string;
    phone: string;
    email: string;
  };
  isNeedToFillInformation: boolean;
  profile: {
    image: string;
  };
  referralCode: string;
  lastCountry: {
    iso2: string;
    name: string;
  };
  addresses: Address[];
  paymentMethods: {
    cards: CreditCard[];
    wallets: Wallet[];
    coupons: Coupon[];
  };
  /** `status` from /esim_app_status/: show the wallet and "pay with crypto" options. */
  isWalletPaymentAllowed: boolean;
  load: (isUpdateCurrency?: boolean) => Promise<void>;
  setNeedToFillInformation: (value: boolean) => void;
  updatePaymentMethods: (
    cards: CreditCard[],
    wallets: Wallet[],
    coupons: Coupon[],
  ) => void;
  clear: () => void;
}

export const useAccountDetailStore = create<AccountDetailState>()(
  persist(
    set => ({
      license: {
        cli: "",
        name: "",
        phone: "",
        email: "",
      },
      isNeedToFillInformation: false,
      profile: {
        image: "",
      },
      referralCode: "",
      lastCountry: {
        iso2: "",
        name: "",
      },
      addresses: [],
      paymentMethods: {
        cards: [],
        wallets: [],
        coupons: [],
      },
      isWalletPaymentAllowed: false,
      load: async (isUpdateCurrency: boolean = false) => {
        // Hidden unless the API says otherwise, also when the call fails.
        axios
          .get(
            "/esim_app_status/" +
              (appStatusApp !== "" ? "?app=" + appStatusApp : ""),
          )
          .then(response => {
            set({ isWalletPaymentAllowed: response.data?.status === true });
          })
          .catch((error: Error) => {
            set({ isWalletPaymentAllowed: false });
            console.error("[account-detail-store] app status:", error.message);
          });

        const mapFormData = new FormData();
        mapFormData.append("action", "get_current_location");
        const mapUrl = "/maps_autocomplete/";
        axios
          .post(mapUrl, mapFormData)
          .then(response => {
            if (isDebugMode()) {
              console.log("Map Autocomplete JSON response:", response.data);
            }
            const lastCountryIso2 = response.data.last_country_iso2;
            set({
              lastCountry: {
                iso2: lastCountryIso2,
                name: response.data.last_country_name,
              },
            });
            let currency = CountryCurrency.find(
              (countryCurrency: CountryCurrencyType) =>
                lastCountryIso2 in countryCurrency,
            );
            currency = currency
              ? currency[lastCountryIso2]
              : process.env.GATSBY_DEFAULT_CURRENCY;
            if (isUpdateCurrency && currency) {
              useConfigurationStore.getState().updateCurrency(currency);
            }

            const paymentMethodFormData = new FormData();
            paymentMethodFormData.append("currency", currency);
            paymentMethodFormData.append(
              "publishable_key",
              getPaymentPublishableKey(),
            );
            const paymentMethodUrl = "/payment_methods/";
            axios
              .post(paymentMethodUrl, paymentMethodFormData)
              .then(response => {
                if (isDebugMode()) {
                  console.log("Payment Method JSON response:", response.data);
                }

                set({
                  paymentMethods: {
                    cards: response.data.cards.filter(
                      (card: CreditCard) =>
                        !isNaN(parseFloat(card.id)) &&
                        !skipCards.includes(card.id),
                    ),
                    wallets: response.data.wallets,
                    coupons: response.data.coupons,
                  },
                });
              })
              .catch(error => {
                if (error.response) {
                  console.error(
                    `[account-detail-store] Response status: ${error.response.status}`,
                  );
                } else {
                  console.error("[account-detail-store]", error.message);
                }
              });
          })
          .catch(error => {
            if (error.response) {
              console.error(
                `[account-detail-store] Response status: ${error.response.status}`,
              );
            } else {
              console.error("[account-detail-store]", error.message);
            }
          });

        const accountDetailFormData = new FormData();
        const accountDetailUrl = "/my_account_details/";
        void axios
          .post(accountDetailUrl, accountDetailFormData)
          .then(response => {
            if (isDebugMode()) {
              console.log("Account Detail JSON response:", response.data);
            }
            if (response.data?.affiliate_code) {
              useConfigurationStore
                .getState()
                .updateAffiliate(response.data.affiliate_code);
            }
            set({
              license: {
                cli: response.data.license_cli,
                name: response.data.license_name,
                phone: response.data.license_phone,
                email: response.data.license_email,
              },
              addresses: (response.data.addresses ?? []).filter(
                (addr: Address) =>
                  addr.id !== "ADD MORE" &&
                  addr.first_name &&
                  addr.first_name !== "ADD MORE",
              ),
            });
          });

        const profileFormData = new FormData();
        const profileImageUrl = "/media/";
        void axios
          .post(profileImageUrl, profileFormData)
          .then(response => {
            set({
              profile: {
                image: response.data.profile_image,
              },
            });
          })
          .catch((error: Error) => {
            console.error("[account-detail] profile image:", error.message);
          });

        const referralFormData = new FormData();
        referralFormData.append("action", "get-ref-code");
        const referralUrl = "/referral/";
        void axios
          .post(referralUrl, referralFormData)
          .then(response => {
            set({
              referralCode: response.data.ref_code,
            });
          })
          .catch((error: Error) => {
            console.error("[account-detail] referral code:", error.message);
          });
      },
      setNeedToFillInformation: (value: boolean) => {
        set({
          isNeedToFillInformation: value,
        });
      },
      updatePaymentMethods: (
        cards: CreditCard[],
        wallets: Wallet[],
        coupons: Coupon[],
      ) => {
        set({
          paymentMethods: {
            cards: cards.filter(
              (card: CreditCard) =>
                !isNaN(parseFloat(card.id)) && !skipCards.includes(card.id),
            ),
            wallets,
            coupons,
          },
        });
      },
      clear: () => {
        set({
          license: {
            cli: "",
            name: "",
            phone: "",
            email: "",
          },
          isNeedToFillInformation: false,
          profile: {
            image: "",
          },
          referralCode: "",
          lastCountry: {
            iso2: "",
            name: "",
          },
          addresses: [],
          paymentMethods: {
            cards: [],
            wallets: [],
            coupons: [],
          },
          isWalletPaymentAllowed: false,
        });
      },
    }),
    {
      name: hashKey("account-detail-storage"),
      skipHydration: true,
      storage: createJSONStorage(() => encryptedStorage),
    },
  ),
);

export const useAccountDetailState = (): AccountDetailState => {
  return useAccountDetailStore(state => {
    return state;
  });
};
